<template>
  <div class="app-container">
    <el-card shadow="never" class="form-card-wrap">
      <div slot="header" class="card-header">
        <span>创建订单</span>
        <div class="card-header-actions">
          <el-button
            type="primary"
            size="small"
            icon="el-icon-full-screen"
            :loading="paymentQrLoading"
            @click="openPaymentQr"
          >查看支付二维码</el-button>
          <el-button type="text" icon="el-icon-refresh" @click="resetForm">重置</el-button>
        </div>
      </div>

      <el-form
        ref="orderForm"
        :model="formData"
        :rules="formRules"
        label-width="100px"
        size="small"
        class="order-el-form"
      >
        <el-divider content-position="left">客户信息</el-divider>
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12">
            <el-form-item label="客户名称" prop="customerName">
              <el-input v-model="formData.customerName" placeholder="请输入客户名称" maxlength="100" />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="客户类型" prop="customerType">
              <el-radio-group v-model="formData.customerType">
                <el-radio :label="CUSTOMER_TYPE.COMPANY">公司</el-radio>
                <el-radio :label="CUSTOMER_TYPE.INDIVIDUAL">个人</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">订单详情</el-divider>
        <el-row :gutter="20">
          <el-col :xs="24" :sm="12">
            <el-form-item label="产品" prop="productId">
              <el-select
                v-model="formData.productId"
                placeholder="请选择产品"
                style="width: 100%;"
                @change="handleProductChange"
              >
                <el-option
                  v-for="product in productList"
                  :key="product.id"
                  :label="`${product.productName}（¥${formatPrice(product.price)}）`"
                  :value="product.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="单价">
              <el-input :value="unitPrice ? ('¥' + formatPrice(unitPrice)) : '等待选择产品'" disabled />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :xs="24" :sm="12">
            <el-form-item label="数量" prop="quantity">
              <el-input-number
                v-model="formData.quantity"
                :min="1"
                controls-position="right"
                style="width: 100%;"
              />
            </el-form-item>
          </el-col>
          <el-col :xs="24" :sm="12">
            <el-form-item label="总金额">
              <el-input
                :value="formData.amount != null ? ('¥' + formatPrice(formData.amount)) : '自动计算'"
                disabled
              />
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider content-position="left">支付凭证</el-divider>
        <el-form-item label="全款截图" required>
          <div class="form-tip">全款支付请上传付款截图</div>
          <el-upload
            action="#"
            :show-file-list="false"
            :http-request="uploadOrderShot"
            accept="image/*"
            drag
            class="upload-box"
          >
            <div v-if="!orderScreenshotPreview" class="upload-empty">
              <i class="el-icon-upload"></i>
              <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
            </div>
            <div v-else class="upload-preview" @click.stop>
              <img :src="orderScreenshotPreview" alt="全款截图">
              <el-button type="danger" icon="el-icon-delete" size="mini" circle class="remove-btn" @click="removeOrderScreenshot" />
            </div>
          </el-upload>
        </el-form-item>

        <el-divider content-position="left">备注信息</el-divider>
        <el-form-item label="订单备注">
          <el-input
            v-model="formData.remark"
            type="textarea"
            :rows="3"
            placeholder="选填，请输入订单相关说明"
            maxlength="500"
            show-word-limit
          />
        </el-form-item>

        <el-form-item>
          <el-button type="primary" :loading="isSubmitting" @click="submitOrder">
            {{ isSubmitting ? (uploadProgress.message || '提交中...') : '提交订单' }}
          </el-button>
          <el-button @click="resetForm">重 置</el-button>
          <el-button @click="$router.push('/')">取 消</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-dialog
      title="支付收款二维码"
      :visible.sync="paymentQrVisible"
      width="420px"
      append-to-body
      :close-on-click-modal="true"
    >
      <div v-loading="paymentQrLoading" class="qr-dialog-body">
        <img v-if="paymentQrUrl" :src="paymentQrUrl" alt="支付二维码" class="qr-image">
        <div v-else-if="!paymentQrLoading" class="qr-empty">
          <i class="el-icon-warning-outline"></i>
          <p>暂未配置收款二维码</p>
          <p class="qr-empty-hint">请管理员在「系统配置管理」中配置 payment_qr</p>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import request from '@/utils/request'
import { mapGetters } from 'vuex'
import {
  CUSTOMER_TYPE,
  PAYMENT_TYPE,
  SCREENSHOT_TYPE
} from '@/utils/enums'

export default {
  name: 'OrderForm',
  data() {
    return {
      CUSTOMER_TYPE,
      PAYMENT_TYPE,
      formData: {
        customerName: '',
        customerType: CUSTOMER_TYPE.COMPANY,
        quantity: 1,
        amount: undefined,
        productId: '',
        paymentType: PAYMENT_TYPE.FULL,
        remark: ''
      },
      formRules: {
        customerName: [{ required: true, message: '请输入客户名称', trigger: 'blur' }],
        customerType: [{ required: true, message: '请选择客户类型', trigger: 'change' }],
        productId: [{ required: true, message: '请选择产品', trigger: 'change' }],
        quantity: [{ required: true, message: '请输入数量', trigger: 'blur' }]
      },
      productList: [],
      unitPrice: 0,
      orderScreenshotPreview: null,
      orderScreenshotUrl: null,
      isSubmitting: false,
      isUploading: false,
      uploadProgress: { message: '' },
      paymentQrVisible: false,
      paymentQrLoading: false,
      paymentQrUrl: ''
    }
  },
  computed: {
    ...mapGetters({
      userInfo: 'user/userInfo'
    })
  },
  watch: {
    'formData.quantity'() {
      this.computeAmount()
    }
  },
  created() {
    this.fetchProducts()
  },
  methods: {
    formatPrice(price) {
      const num = Number(price)
      return Number.isFinite(num) ? num.toFixed(2) : '0.00'
    },
    async openPaymentQr() {
      this.paymentQrVisible = true
      this.paymentQrLoading = true
      this.paymentQrUrl = ''
      try {
        const data = await request.get('/api/config/byKey', {
          params: { configKey: 'payment_qr' }
        })
        const url = data && data.configValue ? String(data.configValue).trim() : ''
        this.paymentQrUrl = url
        if (!url) {
          this.$message.warning('暂未配置收款二维码')
        }
      } catch (error) {
        this.$message.error((error && error.message) || '获取支付二维码失败')
      } finally {
        this.paymentQrLoading = false
      }
    },
    async fetchProducts() {
      try {
        const response = await request.get('/api/product/list')
        this.productList = Array.isArray(response) ? response : []
        if (this.productList.length > 0) {
          this.formData.productId = this.productList[0].id
          this.handleProductChange()
        }
      } catch (error) {
        this.$message.error('获取产品列表失败，请稍后重试')
      }
    },
    handleProductChange() {
      const selected = this.productList.find(item => String(item.id) === String(this.formData.productId))
      this.unitPrice = selected ? Number(selected.price) || 0 : 0
      this.computeAmount()
    },
    computeAmount() {
      const quantity = Number(this.formData.quantity) || 0
      if (!this.unitPrice || !quantity) {
        this.formData.amount = undefined
        return
      }
      this.formData.amount = Number((this.unitPrice * quantity).toFixed(2))
    },
    async uploadFile(file, api) {
      const formData = new FormData()
      formData.append('image', file)
      return request.post(api, formData)
    },
    async uploadOrderShot(option) {
      if (this.isUploading) return
      const file = option.file
      if (!file.type.startsWith('image/')) {
        this.$message.error('请上传图片文件')
        return
      }
      this.isUploading = true
      try {
        this.uploadProgress.message = '正在上传全款截图...'
        const preview = URL.createObjectURL(file)
        const url = await this.uploadFile(file, '/api/common/upload/order')
        this.orderScreenshotPreview = preview
        this.orderScreenshotUrl = url
        this.$message.success('上传成功')
      } catch (error) {
        this.$message.error((error && error.message) || '上传失败')
      } finally {
        this.isUploading = false
        this.uploadProgress.message = ''
      }
    },
    removeOrderScreenshot() {
      this.orderScreenshotPreview = null
      this.orderScreenshotUrl = null
    },
    resetForm() {
      this.formData = {
        customerName: '',
        customerType: CUSTOMER_TYPE.COMPANY,
        quantity: 1,
        amount: undefined,
        productId: this.productList.length ? this.productList[0].id : '',
        paymentType: PAYMENT_TYPE.FULL,
        remark: ''
      }
      this.handleProductChange()
      this.removeOrderScreenshot()
      this.$nextTick(() => {
        if (this.$refs.orderForm) this.$refs.orderForm.clearValidate()
      })
    },
    submitOrder() {
      this.$refs.orderForm.validate(async (valid) => {
        if (!valid) return
        if (!this.orderScreenshotUrl) {
          this.$message.error('请上传全款截图')
          return
        }

        this.isSubmitting = true
        this.uploadProgress.message = '正在创建订单...'
        try {
          const orderData = {
            customerName: this.formData.customerName,
            customerType: Number(this.formData.customerType),
            productId: Number(this.formData.productId),
            orderQuantity: this.formData.quantity,
            totalAmount: this.formData.amount,
            paymentType: Number(this.formData.paymentType),
            depositAmount: null,
            orderRemark: this.formData.remark || '',
            orderDetail: {
              orderScreenshotUrl: this.orderScreenshotUrl
            },
            orderScreenshots: [{
              screenshotType: SCREENSHOT_TYPE.FULL,
              screenshotUrl: this.orderScreenshotUrl
            }]
          }

          await request.post('/api/order', orderData)
          this.$message.success('订单已创建成功！')
          this.$router.push('/history-orders')
        } catch (error) {
          this.$message.error((error && error.message) || '创建订单失败，请重试')
        } finally {
          this.isSubmitting = false
          this.uploadProgress.message = ''
        }
      })
    }
  }
}
</script>

<style scoped>
.app-container {
  width: 100%;
}

.form-card-wrap >>> .el-card__header {
  padding: 12px 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 600;
}

.card-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.order-el-form {
  max-width: 900px;
}

.upload-box {
  width: 360px;
}

.form-tip {
  font-size: 12px;
  color: #909399;
  line-height: 1.4;
  margin-bottom: 8px;
}

.upload-box >>> .el-upload,
.upload-box >>> .el-upload-dragger {
  width: 360px;
}

.upload-empty {
  padding: 30px 0;
}

.upload-preview {
  position: relative;
  width: 360px;
  padding: 8px;
}

.upload-preview img {
  max-width: 100%;
  max-height: 220px;
  display: block;
  margin: 0 auto;
  border-radius: 4px;
}

.remove-btn {
  position: absolute;
  top: 12px;
  right: 12px;
}

.qr-dialog-body {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
}

.qr-image {
  max-width: 100%;
  max-height: 360px;
  border-radius: 4px;
}

.qr-empty {
  color: #909399;
  padding: 24px 0;
}

.qr-empty i {
  font-size: 36px;
  color: #e6a23c;
  margin-bottom: 8px;
}

.qr-empty-hint {
  font-size: 12px;
  margin-top: 6px;
}
</style>
